import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type TouchEvent,
} from 'react';
import { useOutletContext } from 'react-router-dom';
import { ApiError } from '@/api/client';
import { useSession } from '@/auth/SessionContext';
import {
  dateKeyFromDate,
  formatSelectedDayLabel,
  getBookingsForDay,
  sortBookingsByStart,
  type PwaCalendarBooking,
} from '@/calendar/pwaCalendarUtils';
import { BookingDetailSheet } from '@/components/BookingDetailSheet';
import { BookingListCard } from '@/components/BookingListCard';
import type { AppShellOutletContext } from '@/pages/AppShell';

const STATUS_OPTIONS = [
  { value: 'confirmed', label: 'Confirmadas' },
  { value: 'all', label: 'Todos los estados' },
  { value: 'pending_deposit', label: 'Seña pendiente' },
  { value: 'human_supervision', label: 'Supervisión humana' },
  { value: 'cancelled', label: 'Canceladas' },
  { value: 'completed', label: 'Completadas' },
] as const;

function addDays(date: Date, amount: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + amount);
  return next;
}

function parseDateInput(raw: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return null;
  const [year, month, day] = raw.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return Number.isNaN(date.getTime()) ? null : date;
}

function statusMatches(booking: PwaCalendarBooking, status: string): boolean {
  if (status === 'all') return true;
  if (status === 'cancelled') return booking.status.includes('cancel');
  return booking.status === status;
}

export function BookingsSearchPage() {
  const { hasCalendar } = useOutletContext<AppShellOutletContext>();
  const { get } = useSession();
  const [selectedDate, setSelectedDate] = useState(() => new Date());
  const [status, setStatus] = useState('confirmed');
  const [bookings, setBookings] = useState<PwaCalendarBooking[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [detailBooking, setDetailBooking] = useState<PwaCalendarBooking | null>(null);
  const touchStartX = useRef<number | null>(null);
  const activeDayRef = useRef<HTMLButtonElement>(null);

  const loadMonth = useCallback(async () => {
    setLoading(true);
    setLoadErr(null);
    try {
      const params = new URLSearchParams({
        year: String(selectedDate.getFullYear()),
        month: String(selectedDate.getMonth() + 1),
      });
      const data = await get<{ bookings: PwaCalendarBooking[] }>(
        `/api/console/calendario/bookings?${params.toString()}`
      );
      setBookings(data.bookings ?? []);
    } catch (error) {
      setBookings([]);
      setLoadErr(error instanceof ApiError ? error.message : 'No se pudieron cargar las reservas');
    } finally {
      setLoading(false);
    }
  }, [get, selectedDate]);

  useEffect(() => {
    if (hasCalendar !== true) return;
    const timer = window.setTimeout(() => void loadMonth(), 0);
    return () => clearTimeout(timer);
  }, [hasCalendar, loadMonth]);

  useEffect(() => {
    activeDayRef.current?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
  }, [selectedDate]);

  const visibleDates = useMemo(
    () => Array.from({ length: 15 }, (_, index) => addDays(selectedDate, index - 7)),
    [selectedDate]
  );

  const dayBookings = useMemo(
    () =>
      getBookingsForDay(bookings, selectedDate)
        .filter((booking) => statusMatches(booking, status))
        .sort(sortBookingsByStart),
    [bookings, selectedDate, status]
  );

  const changeDay = useCallback((amount: number) => {
    setSelectedDate((current) => addDays(current, amount));
    setDetailBooking(null);
  }, []);

  function handleTouchStart(event: TouchEvent) {
    touchStartX.current = event.touches[0]?.clientX ?? null;
  }

  function handleTouchEnd(event: TouchEvent) {
    if (touchStartX.current === null) return;
    const endX = event.changedTouches[0]?.clientX;
    if (endX === undefined) return;
    const delta = endX - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(delta) < 55) return;
    changeDay(delta < 0 ? 1 : -1);
  }

  if (hasCalendar === null) return <p className="pwa-muted">Cargando…</p>;

  if (hasCalendar === false) {
    return (
      <div className="bookings-search-page">
        <p className="pwa-muted">
          No hay agentes con el plugin de calendario activo en tu organización.
        </p>
      </div>
    );
  }

  return (
    <div
      className="bookings-search-page"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <div className="bookings-search-page__head">
        <h1 className="bookings-search-page__title">Reservas</h1>
        <p className="bookings-search-page__hint">Deslizá para cambiar de día</p>
      </div>

      <div className="bookings-search-page__filters">
        <label>
          <span>Estado</span>
          <select value={status} onChange={(event) => setStatus(event.target.value)}>
            {STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span>Día</span>
          <input
            type="date"
            value={dateKeyFromDate(selectedDate)}
            onChange={(event) => {
              const date = parseDateInput(event.target.value);
              if (date) setSelectedDate(date);
            }}
          />
        </label>
      </div>

      <div className="bookings-search-page__date-nav">
        <button type="button" onClick={() => changeDay(-1)} aria-label="Día anterior">
          ‹
        </button>
        <div className="bookings-search-page__dates" aria-label="Fechas">
          {visibleDates.map((date) => {
            const selected = dateKeyFromDate(date) === dateKeyFromDate(selectedDate);
            return (
              <button
                key={dateKeyFromDate(date)}
                ref={selected ? activeDayRef : undefined}
                type="button"
                className={selected ? 'bookings-search-page__date--active' : ''}
                onClick={() => setSelectedDate(date)}
                aria-pressed={selected}
              >
                <span>{date.toLocaleDateString('es', { weekday: 'short' }).replace('.', '')}</span>
                <strong>{date.getDate()}</strong>
              </button>
            );
          })}
        </div>
        <button type="button" onClick={() => changeDay(1)} aria-label="Día siguiente">
          ›
        </button>
      </div>

      <div className="bookings-search-page__day-heading">
        <strong>{formatSelectedDayLabel(selectedDate)}</strong>
        <span>
          {dayBookings.length} reserva{dayBookings.length === 1 ? '' : 's'}
        </span>
      </div>

      {loading ? <p className="pwa-muted">Cargando reservas…</p> : null}
      {loadErr ? <p className="pwa-err">{loadErr}</p> : null}
      {!loading && !loadErr && dayBookings.length === 0 ? (
        <p className="pwa-muted">No hay reservas para este día y estado.</p>
      ) : null}

      {dayBookings.length > 0 ? (
        <div className="bookings-search-page__list">
          {dayBookings.map((booking) => (
            <BookingListCard
              key={booking.id}
              booking={booking}
              onOpen={() => setDetailBooking(booking)}
            />
          ))}
        </div>
      ) : null}

      {detailBooking ? (
        <BookingDetailSheet
          summary={detailBooking}
          onClose={() => setDetailBooking(null)}
          onChanged={() => {
            void loadMonth();
            setDetailBooking(null);
          }}
        />
      ) : null}
    </div>
  );
}
